/**
 * What the price list is made of.
 *
 * The lines come from `paperman-contract-defaults` — the same constant the
 * builder uses — so this page and a real contract can never disagree about
 * WHICH lines make up a case. Prices come from QuickBooks at read time, so they
 * can never be stale. Nothing about a price is hardcoded here.
 */
import {
  AT_NEED_DEFAULT_LINES,
  ONTARIO_AT_NEED_DEFAULT_LINES,
} from 'paperman-contract-defaults';

export type Region = 'ontario' | 'quebec';

export const REGIONS: { id: Region; label: string }[] = [
  { id: 'ontario', label: 'Ontario' },
  { id: 'quebec', label: 'Quebec' },
];

export const defaultLinesFor = (region: Region) =>
  region === 'ontario' ? ONTARIO_AT_NEED_DEFAULT_LINES : AT_NEED_DEFAULT_LINES;

/**
 * The all-in figure staff quote, agreed 2026-08-26.
 *
 * Deliberately separate from the sum of the lines below it. Ontario's five
 * lines currently total $2,598 against an agreed $2,500 — the page shows both
 * and flags the gap rather than hiding it, because a silent $98 difference
 * between what we quote and what the contract renders surfaces at the worst
 * possible moment. Quebec's single line matches exactly.
 */
export const AGREED_TOTAL: Record<Region, number> = {
  ontario: 2500,
  quebec: 1900,
};

/**
 * What a preneed plan is priced against by default.
 *
 * Not the same as the at-need total: Quebec preneed is $2,100 against an
 * at-need $1,900, while Ontario is $2,500 either way. The agent can type over
 * it — this is just the number they start from.
 */
export const PRENEED_DEFAULT: Record<Region, number> = {
  ontario: 2500,
  quebec: 2100,
};

/**
 * Transport inside the zone is inside these prices (decided 2026-08-26); only a
 * pickup beyond the zone border adds anything. Both provinces now have a zone
 * drawn, but they do not charge the same rate — $3.00/km Ontario, $2.05/km
 * Quebec — so these strings are not interchangeable.
 */
export const TRANSPORT_NOTE: Record<Region, string> = {
  ontario:
    'Transport within Zone 1 is included. A pickup outside Zone 1 adds $3.00/km from the zone border.',
  quebec:
    'Transport within Zone 1 is included. A pickup outside Zone 1 adds $2.05/km from the zone border.',
};

/**
 * Mirrors `region-config.service.ts`. Shown so nobody adds tax to a line that
 * already carries it, or to a disbursement that is out of scope entirely.
 */
export const TAX_NOTE: Record<Region, string> = {
  ontario: 'HST 13% applies to the cremation package. The four disbursements are out of scope.',
  quebec: 'GST 5% + QST 9.975% apply to the package.',
};
